import { useEffect, useRef, useState } from "react"
import { BereichGate } from "@/components/bereich-gesperrt"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import {
  PreparationIntake,
  preparationPrompt,
} from "@/components/preparation-intake"
import { GeneralCoachChat } from "@/components/coach-chat-general"
import { toast } from "sonner"
import { useCoachThread, useCreateCoachThread } from "@/lib/queries"
import { useCoach } from "@/lib/use-coach"
import { useSessionContext } from "@/lib/session"
import { parseChatSearch } from "@/lib/chat-navigation"
import { threadDestination } from "@/lib/recent-entries"
import { PREPARATION_START_PROMPTS } from "@/lib/coach-prompts"
import { COACH_FALLBACK_LABEL } from "@nl/shared"

export function PreparationPage() {
  const { role } = useSessionContext()
  const coach = useCoach()
  const name = coach?.name ?? COACH_FALLBACK_LABEL
  const navigate = useNavigate()
  const { gespraech } = Route.useSearch()
  const [initialPrompt, setInitialPrompt] = useState<string>()
  const [voiceThreadId, setVoiceThreadId] = useState<string>()
  const createThread = useCreateCoachThread()
  const previousThread = useRef(gespraech)
  useEffect(() => {
    if (previousThread.current && previousThread.current !== gespraech)
      setInitialPrompt(undefined)
    previousThread.current = gespraech
  }, [gespraech])
  const { data: thread } = useCoachThread(gespraech)
  // A newly created empty thread receives its mode with the first message.
  const differentMode = thread?.mode && thread.mode !== "preparation"
  useEffect(() => {
    if (differentMode)
      void navigate({ ...threadDestination(thread), replace: true })
  }, [differentMode, thread, navigate])
  useEffect(() => {
    if (role === "coach") void navigate({ to: "/coach", replace: true })
    if (role === "manager") void navigate({ to: "/management", replace: true })
  }, [role, navigate])
  if (differentMode) return null
  if (!gespraech && !initialPrompt)
    return (
      <PreparationIntake
        coachName={name}
        pending={createThread.isPending}
        onStart={async (context, channel) => {
          if (channel === "chat") {
            setInitialPrompt(preparationPrompt(context))
            return
          }
          try {
            const created = await createThread.mutateAsync({
              body: {
                startContext: preparationPrompt(context),
                startMode: "preparation",
              },
            })
            setVoiceThreadId(created.id)
            await navigate({
              to: "/gespraechsauftakt",
              search: { gespraech: created.id },
              replace: true,
            })
          } catch {
            toast.error(
              "Das Live-Gespräch konnte nicht vorbereitet werden. Deine Angaben bleiben erhalten. Bitte versuche es erneut oder nutze den Chat."
            )
          }
        }}
      />
    )
  return (
    <GeneralCoachChat
      threadId={gespraech}
      initialPrompt={initialPrompt}
      autoStartVoice={!!gespraech && gespraech === voiceThreadId}
      mode="preparation"
      barSize="md"
      autoFocus
      onThreadCreated={(id) =>
        void navigate({
          to: "/gespraechsauftakt",
          search: { gespraech: id },
          replace: true,
        })
      }
      onReset={() => {
        setInitialPrompt(undefined)
        setVoiceThreadId(undefined)
        void navigate({
          to: "/gespraechsauftakt",
          search: {},
          replace: true,
        })
      }}
      aboveThread={
        <div className="mb-6">
          <h1 className="text-base font-medium">Terminvorbereitung</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Dein nächster Termin, gemeinsam mit {name} vorbereitet.
          </p>
        </div>
      }
      empty={{
        title:
          "Welches Gespräch steht an und wobei möchtest du sicherer werden?",
        hint: "Erzähl kurz vom Anlass und deinem Ziel. Gemeinsam finden wir einen passenden Einstieg, gute Fragen und Antworten auf mögliche Einwände. Bitte ohne Namen oder andere persönliche Kundendaten.",
        prompts: PREPARATION_START_PROMPTS,
      }}
      placeholder="Zum Beispiel: Morgen kommt jemand zum Jahresgespräch …"
    />
  )
}
function PreparationPageGesperrt() {
  const { gespraech } = Route.useSearch()
  // An existing conversation stays reachable; the lock only stops a new one.
  return (
    <BereichGate bereich="gespraechsauftakt" offen={Boolean(gespraech)}>
      <PreparationPage />
    </BereichGate>
  )
}

export const Route = createFileRoute("/_app/gespraechsauftakt")({
  validateSearch: (
    search: Record<string, unknown>
  ): { gespraech?: string } => ({
    gespraech: parseChatSearch(search).gespraech,
  }),
  component: PreparationPageGesperrt,
})
